import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Layers } from "lucide-react";
import { TIPOS_MODALIDAD } from "@/lib/constants";
import { nombreTourDisplay } from "@/lib/tourDisplay";

function salidaLabel(s, mostrarTour) {
  const modalidad = TIPOS_MODALIDAD[s.modalidad] || s.modalidad;
  const veh = s.vehiculo === "van" ? "Van" : "Bus";
  const idioma = s.idioma === "combinada" ? "Bilingüe" : s.idioma === "en" ? "Inglés" : "Español";
  const prefijo = mostrarTour && s.tour_nombre ? `${nombreTourDisplay(s.tour_nombre, s.modalidad)} · ` : "";
  return `${prefijo}${modalidad} · ${veh} · ${idioma} (cupo ${s.cupo_max})`;
}

export default function CombinarHorarioDialog({ open, onOpenChange, items = [], fecha, onDone }) {
  // Grupos candidatos: mismo HORARIO (fecha ya fijada por la Agenda del día), con 2+
  // salidas independientes -- pueden ser de tours DISTINTOS (ej. Washington DC +
  // Washington VIP a las 06:00). No incluye privados ni ya combinadas entre sí como
  // una sola opción -- se puede reconfigurar.
  const opciones = useMemo(() => {
    const map = {};
    for (const it of items) {
      if (it.es_privado) continue;
      const key = it.hora;
      map[key] = map[key] || { hora: it.hora, salidas: [] };
      if (it.es_grupo) {
        for (const m of it.miembros || []) map[key].salidas.push(m);
      } else {
        map[key].salidas.push(it);
      }
    }
    return Object.values(map).filter((g) => g.salidas.length >= 2);
  }, [items]);

  const [key, setKey] = useState("");
  const [seleccion, setSeleccion] = useState({});
  const [cupoOverride, setCupoOverride] = useState("");
  const [nota, setNota] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setKey(opciones[0]?.hora || "");
      setCupoOverride("");
      setNota("");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const grupo = opciones.find((g) => g.hora === key);
  const toursDistintos = grupo ? new Set(grupo.salidas.map((s) => s.tour_nombre)).size > 1 : false;

  useEffect(() => {
    if (grupo) {
      const sel = {};
      for (const s of grupo.salidas) sel[s.id] = true;
      setSeleccion(sel);
    }
  }, [grupo]);

  const seleccionadas = grupo ? grupo.salidas.filter((s) => seleccion[s.id]) : [];
  const sumaCupo = seleccionadas.reduce((acc, s) => acc + (s.cupo_max || 0), 0);

  const combinar = async () => {
    if (!grupo || seleccionadas.length < 2) {
      toast.error("Elegí al menos 2 salidas para combinar.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/salidas/grupo", {
        fecha,
        hora: grupo.hora,
        salida_ids: seleccionadas.map((s) => s.id),
        cupo_override: cupoOverride ? Number(cupoOverride) : null,
        nota,
      });
      toast.success(`Horario combinado: cupo total de ${cupoOverride || sumaCupo} pasajeros.`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="combinar-horario-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Layers className="h-5 w-5 text-indigo-600" /> Combinar cupo por horario
          </DialogTitle>
          <DialogDescription>
            Uní varias salidas del MISMO horario (distinta modalidad, vehículo, idioma -- incluso de
            TOURS distintos, ej. Washington DC + Washington VIP) en un único cupo real. Ej: bus
            bilingüe (52) + van VIP (13) = 65 asientos reales, sin doble conteo.
          </DialogDescription>
        </DialogHeader>

        {opciones.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">
            No hay dos o más salidas en el mismo horario hoy para combinar.
          </p>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-slate-500">Horario</Label>
              <Select value={key} onValueChange={setKey}>
                <SelectTrigger data-testid="combinar-horario-select" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {opciones.map((g) => {
                    const tours = [...new Set(g.salidas.map((s) => nombreTourDisplay(s.tour_nombre, s.modalidad)))];
                    return (
                      <SelectItem key={g.hora} value={g.hora}>
                        {g.hora} · {tours.join(" + ")} ({g.salidas.length} salidas)
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {grupo && (
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Salidas a combinar</Label>
                {grupo.salidas.map((s) => (
                  <label
                    key={s.id}
                    className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm cursor-pointer"
                    data-testid={`combinar-check-${s.id}`}
                  >
                    <Checkbox
                      checked={!!seleccion[s.id]}
                      onCheckedChange={(v) => setSeleccion((prev) => ({ ...prev, [s.id]: !!v }))}
                    />
                    {salidaLabel(s, toursDistintos)}
                  </label>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Cupo total (opcional)</Label>
                <Input
                  type="number"
                  min={0}
                  placeholder={String(sumaCupo)}
                  value={cupoOverride}
                  onChange={(e) => setCupoOverride(e.target.value)}
                  data-testid="combinar-cupo-override"
                  className="mt-1"
                />
                <p className="text-[11px] text-slate-400 mt-1">Vacío = suma automática ({sumaCupo}).</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Nota (opcional)</Label>
                <Input
                  placeholder="Ej: 1 bus + 1 van"
                  value={nota}
                  onChange={(e) => setNota(e.target.value)}
                  data-testid="combinar-nota-input"
                  className="mt-1"
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={combinar} disabled={saving || !grupo || seleccionadas.length < 2} data-testid="combinar-confirmar-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : `Combinar ${seleccionadas.length} salidas`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
